import { useEffect } from 'react'
import { usePostHog } from '@posthog/react'
import { useUser, useClientOptions } from '@paintscout/ui-business'
import { useFlags } from 'shared/components/common/FlagsProvider'
import * as Sentry from '@sentry/react'
import { initAnalytics, track } from 'shared/util/analytics'
import { isE2E } from 'shared/util/e2e'

export default function PostHogIdentify(): JSX.Element {
  const posthog = usePostHog()
  const { options } = useClientOptions()
  const { user, highestRole, painterType } = useUser()
  const { posthogSessionReplay: sessionReplayEnabled } = useFlags()

  useEffect(() => {
    if (!posthog || !user?.user_id || !options) return
    if (!process.env.POSTHOG_PROJECT_TOKEN) return

    const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
    const standalone = 'standalone' in window.navigator && (window.navigator as any).standalone

    // Read before `identify` below, which is what flips this to true.
    const alreadyIdentified = posthog._isIdentified()

    // `identify` is safe to re-run: posthog-js dedupes repeat calls for the same
    // distinct_id via its cached person properties. The `posthog.group()` call that used
    // to sit alongside it was removed — group analytics is not an add-on we subscribe
    // to, so it only ever cost us a billable `$groupidentify` per effect run.
    posthog.identify(user.user_id, {
      name: user.name,
      email: user.email,
      companyId: user.app_metadata.companyId,
      // `company`/`companyId` are person properties, so company-level breakdowns still
      // work without PostHog's group-analytics add-on (which we don't subscribe to).
      company: options.options.companyName.value,
      iPadOS,
      standalone,
      role: highestRole ?? null,
      painterType: painterType ?? null
    })

    const isPainter = highestRole === 'painter'
    const shouldRecord = (isPainter || !!sessionReplayEnabled) && !isE2E()
    initAnalytics(shouldRecord)

    // This effect re-runs whenever `options` changes identity — at minimum twice per
    // load, since ClientOptionsProvider queries appDocs with `cache-and-network`, and
    // again after every options save. `track` has no dedupe of its own, so guard on
    // whether PostHog already considers this user identified.
    if (!alreadyIdentified) {
      track('user_logged_in')
    }

    const sessionReplayUrl = posthog.get_session_replay_url()
    if (sessionReplayUrl) {
      Sentry.configureScope((scope) => {
        scope.setExtra('postHogSessionURL', sessionReplayUrl)
      })
    }
  }, [posthog, options, user?.user_id, highestRole, painterType, sessionReplayEnabled])

  return null
}
